/**
 * @file THE LANE GRAPH: `level.map.roads` turned into a directed network a car
 * can actually drive. Pure data — zero three.js, zero rng, zero allocation in
 * the query path — so the whole thing is provable in node
 * (`tests/traffic.test.mjs`) and identical on every machine.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO EDGE KINDS, AND WHY THE SECOND ONE EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *   'lane'  a one-way carriageway between two junctions, laterally offset from
 *           the road centre-line (right-hand traffic).
 *   'link'  the piece THROUGH a junction: a cubic-Bezier fillet from one lane's
 *           exit point to the next lane's entry point.
 *
 * The link is not decoration. A lane is offset sideways, so lane A's last point
 * and lane B's first point are DIFFERENT POINTS — a car that hopped straight
 * from one to the other would teleport across the junction mouth, by 6 m on a
 * boulevard-to-street right turn. The link carries it across, and it also gives
 * the junction-yield rule something concrete to own: a car is "in the box"
 * exactly while it is on a link whose `node` is that junction, so
 * `Traffic.js`'s one-car-at-a-time claim is a property of the graph rather than
 * a radius test.
 *
 * Successors are precomputed per EDGE (`edge.next`), not per node: an arriving
 * car asks "where can I go from here", and at a junction that answer depends on
 * where it came from (no U-turns except at a dead end). {@link
 * LaneGraph#outgoing} is the spelled-out form of the same array.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RIGHT-HAND TRAFFIC, AND THE ONE SIGN THAT DECIDES IT
 * ─────────────────────────────────────────────────────────────────────────────
 * For a heading `f = (fx, fz)` the car's right-hand side is `(-fz, fx)`. That is
 * not a convention picked here — it is the vehicle stack's: `carSpec.js` states
 * "+X is the nose, −Z is the driver/left side", and at yaw 0 (`f = (1,0)`) the
 * formula gives `(0,1)` = +Z = the passenger side. So a lane centre-line is the
 * road centre-line plus `right * offset`, and yaw for a heading is
 * `atan2(-fz, fx)` (a `Matrix4.makeRotationY(yaw)` maps +X to `(cos, -sin)`).
 * Get either of those backwards and the whole city drives on the left.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT COMES OUT OF `level.map`
 * ─────────────────────────────────────────────────────────────────────────────
 * `roads.lattice` is the city's `RoadEdge[]` verbatim: `ax/az`→`bx/bz` is the
 * centre line between two crossing centres, `axis` says which line array it
 * belongs to ('x' = constant x, RUNS along Z), and `width`/`class` size it. The
 * junction boxes are re-derived here from those widths, because `level.map`
 * publishes no intersection list — a node's X extent is the widest 'x'-axis
 * road at it and its Z extent the widest 'z'-axis one, which is exactly the
 * city's own `Intersection.w/d`.
 *
 * `roads.country` is centre-line polylines. A `kind: 'head'` entry is a
 * cul-de-sac turning circle carrying only its centre — it becomes the U-turn at
 * the end of the radial it belongs to. Where a country road meets the city its
 * first few metres are TRIMMED and handed to a link, so the 22 m boulevard's
 * ±4.5 m lane tapers into the 7 m country road's ±2 m over a dozen metres
 * instead of jinking sideways on the spot.
 *
 * @module engine/traffic/LaneGraph
 */

/** @typedef {import('../types.js').Vec2XZ} Vec2XZ */

/**
 * Lane centre-line offset from the road centre-line, per road class. Each is
 * comfortably inside its own carriageway AND clear of the main street's 2.4 m
 * median (the boulevard's 4.5 m sits between the 1.2 m median edge and the
 * 11 m kerb).
 */
export const LANE_OFFSET = Object.freeze({
  // ⚠ THERE IS NO `alley` ROW HERE OR IN `SPEED_LIMIT`, AND THERE MUST NEVER BE
  // ONE. See {@link NO_TRAFFIC_CLASSES} — `tests/traffic.test.mjs` asserts the
  // absence, because adding a row is exactly how somebody would "fix" an alley
  // that looked like it was missing from the graph.
  boulevard: 4.5,
  avenue: 3.0,
  street: 2.2,
  country: 2.0,
  // The out-of-town TIERS (`levels/city/config.js#country.tiers`). Each is a
  // quarter of its road's width, i.e. the middle of its own half-carriageway —
  // the same relationship `country`'s 2.0 has to a 7 m lane. They are not
  // taste: a 16 m highway with a 2 m offset is two cars driving down the
  // hatching, and a 5 m track with a 4 m offset is two cars in the ditch.
  highway: 4.0,
  trunk: 2.5,
  dirt: 1.3,
});

/** Free-flow speed per road class, m/s. */
export const SPEED_LIMIT = Object.freeze({
  boulevard: 12,
  avenue: 11,
  street: 8,
  country: 13,
  // ⚠ `country` IS NO LONGER THE CEILING. It was, and one assertion used to say
  // so; the ring is the fastest road in the level by design.
  highway: 17,
  trunk: 14,
  dirt: 9,
});

/**
 * Route KINDS that are also lane CLASSES.
 *
 * Every other country route (`radial`, `loop`, `link`) is class `'country'` —
 * they are the same road with different planning provenance, and a car has no
 * business driving a link any differently from the radial it joins.
 * @type {ReadonlySet<string>}
 */
export const TIER_CLASSES = Object.freeze(new Set(['highway', 'trunk', 'dirt']));

/**
 * ROAD CLASSES NO CAR MAY EVER USE. Filtered out of `map.roads.lattice` before
 * a node, a box or a lane is derived from it — and out of `Traffic.js`'s parked
 * bays, which read the same array.
 *
 * `'alley'` is a 4.5 m pedestrian cut through the middle of a block
 * (`levels/city/roads.js#alleyRules`). Two things go wrong if it merely lacks a
 * `LANE_OFFSET` row instead of being refused here, and only the second is
 * visible:
 *
 *  • `LANE_OFFSET[e.class] ?? LANE_OFFSET.street` and its `SPEED_LIMIT` twin
 *    both fall back, so an alley would come out as an 8 m/s street lane offset
 *    2.2 m inside a 2.25 m half-carriageway — traffic driving down an alley,
 *    through the kerb, with nothing anywhere reporting it.
 *  • An alley's two ends land in the MIDDLE of the paved rect of the road it
 *    opens off, nowhere near a lattice crossing, so `exactNode` would mint two
 *    `'junction'` nodes there. Each gets a `DEFAULT_WIDTH` box on the axis with
 *    no road — a claimable junction in the middle of a straight, which every
 *    car in town would yield at.
 *
 * ⚠ IT IS A REFUSAL, NOT AN OMISSION. Do not "complete" `LANE_OFFSET` /
 * `SPEED_LIMIT` with an alley row.
 * @type {ReadonlySet<string>}
 */
export const NO_TRAFFIC_CLASSES = Object.freeze(new Set(['alley']));

/** Cap for a straight-through junction link, m/s. */
export const LINK_SPEED = 9;
/** Cap for a turning junction link, m/s — a right-angle turn at 9 m/s reads as ice. */
export const TURN_SPEED = 5.5;

/** Metres of a country road handed to the link that joins it to the lattice. */
const CITY_JOIN_TRIM = 12;
/** How far a country route's end may be from a lattice node and still join it. */
const CITY_JOIN_RADIUS = 26;
/** Two country route ends closer than this are the SAME node. */
const JOINT_MERGE = 2.0;
/** Fallback junction extent when a node sees no road on one axis. */
const DEFAULT_WIDTH = 9;
/** Samples per link polyline (the Bezier fillet). */
const LINK_SAMPLES = 7;
/** Below this gap AND turn angle, two country lanes are wired directly. */
const DIRECT_GAP = 0.3;
const DIRECT_TURN = 0.09;
/**
 * How far a published `joins` point may be from the nearest polyline VERTEX and
 * still split the route there, metres.
 *
 * A junction point is either a vertex copied verbatim (a route that ends on
 * another one) or a segment crossing, so half a `country.sample` — 2 m — is the
 * honest bound. 6 leaves room for `roundJoints` having resampled a corner at
 * half pitch, and is still far under the `country.join.spacing` 34 m that keeps
 * two junctions from landing on one vertex.
 */
const MID_JOIN_GAP = 6;
/** Polyline vertices a split must leave at each end, so no piece is a stub. */
const MID_JOIN_EDGE = 2;
/** ...and between two splits, for the same reason. */
const MID_JOIN_SPAN = 3;
/** Segment bucket size for {@link LaneGraph#nearestLane}, metres. */
const NEAR_CELL = 16;

/**
 * @typedef {Object} LaneNode
 * @property {number} id
 * @property {number} x @property {number} z
 * @property {'junction'|'joint'} kind `junction` = a lattice crossing, and the
 *   only kind that carries a claimable box.
 * @property {{x: number, z: number, w: number, d: number}|null} box Paved
 *   crossing rect, MIN corner (the project-wide convention). `null` off-lattice.
 */

/**
 * @typedef {Object} LaneEdge
 * @property {number} id Index into {@link LaneGraph#edges}.
 * @property {number} from @property {number} to Node ids.
 * @property {Vec2XZ[]} points Centre line of the LANE, >= 2 points.
 * @property {number} speedLimit m/s.
 * @property {'lane'|'link'} kind
 * @property {string} cls Road class, or `'link'`.
 * @property {number} node Junction node this link crosses; −1 for a lane and
 *   for an off-lattice joint link (nothing to claim there).
 * @property {number} turn −1 left, 0 straight, +1 right. Lanes are 0.
 * @property {boolean} uTurn
 * @property {number} length Metres.
 * @property {Float64Array} cum Cumulative arc length, `points.length` entries.
 * @property {number[]} next Successor edge ids.
 * @property {number} rev The opposite carriageway of the same road; −1 on links.
 * @property {string} [src] Source road id, for debugging.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The graph
// ─────────────────────────────────────────────────────────────────────────────

export class LaneGraph {
  /**
   * @param {LaneNode[]} nodes
   * @param {LaneEdge[]} edges
   * @param {Object} [info] Build diagnostics, merged into {@link stats}.
   */
  constructor(nodes, edges, info = {}) {
    /** @type {LaneNode[]} */
    this.nodes = nodes;
    /** @type {LaneEdge[]} */
    this.edges = edges;
    /** @type {Object} */
    this.info = info;

    /** Lane (non-link) edge ids — what a spawner and `nearestLane` consider. */
    this.laneEdges = [];
    for (const e of edges) if (e.kind === 'lane') this.laneEdges.push(e.id);

    this._buildNearIndex();
  }

  /**
   * Successors of `arrivingEdgeId`. `nodeId` is accepted (and validated in the
   * dev build) because callers think in junctions; the answer is precomputed
   * per edge, because at a junction it depends on where you came from.
   *
   * @param {number} nodeId Node the car is at. Pass −1 to skip the check.
   * @param {number} arrivingEdgeId
   * @returns {number[]} REUSED — never mutate it.
   */
  outgoing(nodeId, arrivingEdgeId) {
    const e = this.edges[arrivingEdgeId];
    if (!e) return EMPTY;
    if (nodeId >= 0 && e.to !== nodeId) return EMPTY;
    return e.next;
  }

  /** @param {number} edgeId @returns {number} Metres. */
  edgeLength(edgeId) {
    return this.edges[edgeId]?.length ?? 0;
  }

  /**
   * Position and unit heading at arc length `s` along an edge. Clamped at both
   * ends, so a caller may over-run without a guard.
   *
   * @param {number} edgeId
   * @param {number} s Metres from the edge's start.
   * @param {{x: number, z: number, dx: number, dz: number}} [out] Reused.
   * @returns {{x: number, z: number, dx: number, dz: number}}
   */
  sampleAt(edgeId, s, out = { x: 0, z: 0, dx: 1, dz: 0 }) {
    const e = this.edges[edgeId];
    if (!e) {
      out.x = 0;
      out.z = 0;
      out.dx = 1;
      out.dz = 0;
      return out;
    }
    const pts = e.points;
    const cum = e.cum;
    const n = pts.length;
    const t = s <= 0 ? 0 : s >= e.length ? e.length : s;

    // Linear scan: an edge is 2 points (lane) or 7 (link), so a binary search
    // would cost more in branches than it saves in steps.
    let i = 1;
    while (i < n - 1 && cum[i] < t) i++;
    const a = pts[i - 1];
    const b = pts[i];
    const segLen = cum[i] - cum[i - 1];
    const u = segLen > 1e-9 ? (t - cum[i - 1]) / segLen : 0;
    out.x = a.x + (b.x - a.x) * u;
    out.z = a.z + (b.z - a.z) * u;
    const inv = segLen > 1e-9 ? 1 / segLen : 0;
    out.dx = (b.x - a.x) * inv;
    out.dz = (b.z - a.z) * inv;
    if (inv === 0) {
      out.dx = 1;
      out.dz = 0;
    }
    return out;
  }

  /**
   * Nearest point on any LANE edge (links are junction interiors — nothing
   * should be spawned or carjacked into one).
   *
   * @param {number} x @param {number} z
   * @param {{edge: number, s: number, x: number, z: number, dist: number}} [out]
   * @returns {{edge: number, s: number, x: number, z: number, dist: number}}
   *   `edge` is −1 when the graph is empty.
   */
  nearestLane(x, z, out = { edge: -1, s: 0, x: 0, z: 0, dist: Infinity }) {
    out.edge = -1;
    out.dist = Infinity;
    const idx = this._near;
    if (!idx) return out;

    // Expanding ring search. A hit in ring r is not necessarily the nearest —
    // a segment `d` metres away can only hide in rings up to `d / cell`, so the
    // first hit shrinks the search bound instead of ending the search.
    let limit = idx.maxRing;
    for (let ring = 0; ring <= limit; ring++) {
      this._searchRing(x, z, ring, out);
      if (out.edge >= 0) {
        const need = ring + 1 + Math.ceil(out.dist / NEAR_CELL);
        if (need < limit) limit = need;
      }
    }
    return out;
  }

  /**
   * @returns {{nodes: number, edges: number, lanes: number, links: number,
   *   junctions: number, pruned: number, connected: boolean, metres: number}}
   */
  stats() {
    let lanes = 0;
    let links = 0;
    let metres = 0;
    for (const e of this.edges) {
      if (e.kind === 'lane') lanes++;
      else links++;
      metres += e.length;
    }
    let junctions = 0;
    for (const n of this.nodes) if (n.box) junctions++;
    return {
      nodes: this.nodes.length,
      edges: this.edges.length,
      lanes,
      links,
      junctions,
      pruned: this.info.pruned ?? 0,
      connected: this.info.connected ?? false,
      metres: Math.round(metres),
    };
  }

  // ── internals ──────────────────────────────────────────────────────────────

  _buildNearIndex() {
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    for (const id of this.laneEdges) {
      for (const p of this.edges[id].points) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.z < minZ) minZ = p.z;
        if (p.z > maxZ) maxZ = p.z;
      }
    }
    if (!Number.isFinite(minX)) {
      this._near = null;
      return;
    }
    const nx = Math.max(1, Math.ceil((maxX - minX) / NEAR_CELL) + 1);
    const nz = Math.max(1, Math.ceil((maxZ - minZ) / NEAR_CELL) + 1);
    /** @type {Array<number[]|undefined>} bucket -> packed (edge<<12 | seg) */
    const cells = new Array(nx * nz);
    for (const id of this.laneEdges) {
      const pts = this.edges[id].points;
      for (let s = 0; s + 1 < pts.length; s++) {
        const a = pts[s];
        const b = pts[s + 1];
        const gx0 = clampI(Math.floor((Math.min(a.x, b.x) - minX) / NEAR_CELL), 0, nx - 1);
        const gx1 = clampI(Math.floor((Math.max(a.x, b.x) - minX) / NEAR_CELL), 0, nx - 1);
        const gz0 = clampI(Math.floor((Math.min(a.z, b.z) - minZ) / NEAR_CELL), 0, nz - 1);
        const gz1 = clampI(Math.floor((Math.max(a.z, b.z) - minZ) / NEAR_CELL), 0, nz - 1);
        for (let gz = gz0; gz <= gz1; gz++) {
          for (let gx = gx0; gx <= gx1; gx++) {
            (cells[gz * nx + gx] ||= []).push(id * 4096 + s);
          }
        }
      }
    }
    this._near = { minX, minZ, nx, nz, cells, maxRing: Math.max(nx, nz) };
  }

  /**
   * @param {number} x @param {number} z @param {number} ring
   * @param {{edge: number, s: number, x: number, z: number, dist: number}} out
   */
  _searchRing(x, z, ring, out) {
    const idx = this._near;
    const cx = Math.floor((x - idx.minX) / NEAR_CELL);
    const cz = Math.floor((z - idx.minZ) / NEAR_CELL);
    for (let gz = cz - ring; gz <= cz + ring; gz++) {
      if (gz < 0 || gz >= idx.nz) continue;
      for (let gx = cx - ring; gx <= cx + ring; gx++) {
        if (gx < 0 || gx >= idx.nx) continue;
        // Ring, not disc: the interior was searched by a previous pass.
        if (ring > 0 && gx !== cx - ring && gx !== cx + ring && gz !== cz - ring && gz !== cz + ring) continue;
        const list = idx.cells[gz * idx.nx + gx];
        if (!list) continue;
        for (let k = 0; k < list.length; k++) {
          const packed = list[k];
          const id = (packed / 4096) | 0;
          const si = packed - id * 4096;
          const e = this.edges[id];
          const a = e.points[si];
          const b = e.points[si + 1];
          const abx = b.x - a.x;
          const abz = b.z - a.z;
          const len2 = abx * abx + abz * abz;
          let u = len2 > 1e-12 ? ((x - a.x) * abx + (z - a.z) * abz) / len2 : 0;
          u = u < 0 ? 0 : u > 1 ? 1 : u;
          const px = a.x + abx * u;
          const pz = a.z + abz * u;
          const d = Math.hypot(x - px, z - pz);
          if (d < out.dist) {
            out.dist = d;
            out.edge = id;
            out.s = e.cum[si] + Math.sqrt(len2) * u;
            out.x = px;
            out.z = pz;
          }
        }
      }
    }
  }
}

/** @type {number[]} Shared empty successor list — never mutate. */
const EMPTY = Object.freeze([]);

// ─────────────────────────────────────────────────────────────────────────────
// Build
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the lane graph from a {@link import('../types.js').LevelMapData}.
 * Deterministic and rng-free: the same map always yields the same graph, edge
 * ids included.
 *
 * @param {import('../types.js').LevelMapData|null|undefined} map
 * @param {Object} [o]
 * @param {boolean} [o.country=true] Include the out-of-town network.
 * @returns {LaneGraph}
 */
export function buildLaneGraph(map, o = {}) {
  const { country: withCountry = true } = o;
  // ⚠ FILTERED ONCE, HERE, so every pass below (nodes, boxes, lanes) sees the
  // same array and none of them has to remember the rule. See
  // {@link NO_TRAFFIC_CLASSES}.
  const lattice = (map?.roads?.lattice ?? []).filter((e) => !NO_TRAFFIC_CLASSES.has(e?.class));
  const routes = withCountry ? (map?.roads?.country ?? []) : [];

  /** @type {LaneNode[]} */
  const nodes = [];
  /** @type {Map<string, number>} */
  const byKey = new Map();
  /** Per-node paved extents, before the boxes are frozen. */
  const extX = [];
  const extZ = [];

  /** @param {number} x @param {number} z @param {'junction'|'joint'} kind */
  const exactNode = (x, z, kind) => {
    const key = `${Math.round(x * 100)}|${Math.round(z * 100)}`;
    let i = byKey.get(key);
    if (i === undefined) {
      i = nodes.length;
      nodes.push({ id: i, x, z, kind, box: null });
      extX.push(0);
      extZ.push(0);
      byKey.set(key, i);
    }
    return i;
  };

  /** Proximity merge, for country ends that were sampled independently. */
  const jointNode = (x, z) => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].kind !== 'joint') continue;
      if (Math.abs(nodes[i].x - x) <= JOINT_MERGE && Math.abs(nodes[i].z - z) <= JOINT_MERGE) return i;
    }
    return exactNode(x, z, 'joint');
  };

  // ── 1. lattice nodes + their junction boxes ───────────────────────────────
  for (const e of lattice) {
    const a = exactNode(e.ax, e.az, 'junction');
    const b = exactNode(e.bx, e.bz, 'junction');
    if (e.axis === 'x') {
      extX[a] = Math.max(extX[a], e.width);
      extX[b] = Math.max(extX[b], e.width);
    } else {
      extZ[a] = Math.max(extZ[a], e.width);
      extZ[b] = Math.max(extZ[b], e.width);
    }
  }
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (n.kind !== 'junction') continue;
    const w = extX[i] || DEFAULT_WIDTH;
    const d = extZ[i] || DEFAULT_WIDTH;
    n.box = { x: n.x - w / 2, z: n.z - d / 2, w, d };
  }

  /** @type {LaneEdge[]} */
  const edges = [];

  /**
   * @param {number} from @param {number} to @param {Vec2XZ[]} points
   * @param {number} speedLimit @param {string} cls
   * @param {'lane'|'link'} kind
   * @param {Object} [extra]
   * @returns {number} Edge id, or −1 when the polyline was degenerate.
   */
  const addEdge = (from, to, points, speedLimit, cls, kind, extra = {}) => {
    const cum = new Float64Array(points.length);
    let len = 0;
    for (let i = 1; i < points.length; i++) {
      len += Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
      cum[i] = len;
    }
    if (!(len > 1e-4)) return -1;
    const id = edges.length;
    edges.push({
      id,
      from,
      to,
      points,
      speedLimit,
      kind,
      cls,
      node: -1,
      turn: 0,
      uTurn: false,
      length: len,
      cum,
      next: [],
      rev: -1,
      ...extra,
    });
    return id;
  };

  // ── 2. lattice lanes ──────────────────────────────────────────────────────
  for (const e of lattice) {
    const a = exactNode(e.ax, e.az, 'junction');
    const b = exactNode(e.bx, e.bz, 'junction');
    const total = Math.hypot(e.bx - e.ax, e.bz - e.az);
    if (!(total > 1e-4)) continue;
    const fx = (e.bx - e.ax) / total;
    const fz = (e.bz - e.az) / total;
    const off = LANE_OFFSET[e.class] ?? LANE_OFFSET.street;
    const speed = SPEED_LIMIT[e.class] ?? SPEED_LIMIT.street;

    /** @type {number[]} */
    const pair = [];
    for (const sign of [1, -1]) {
      const dx = fx * sign;
      const dz = fz * sign;
      // Right-hand traffic: right = (-dz, dx). See the header.
      const rx = -dz * off;
      const rz = dx * off;
      const P = nodes[sign > 0 ? a : b];
      const Q = nodes[sign > 0 ? b : a];
      const hP = halfBox(P, dx, dz);
      const hQ = halfBox(Q, dx, dz);
      const p0 = { x: P.x + dx * hP + rx, z: P.z + dz * hP + rz };
      const p1 = { x: Q.x - dx * hQ + rx, z: Q.z - dz * hQ + rz };
      const id = addEdge(sign > 0 ? a : b, sign > 0 ? b : a, [p0, p1], speed, e.class, 'lane', { src: e.id });
      if (id >= 0) pair.push(id);
    }
    if (pair.length === 2) {
      edges[pair[0]].rev = pair[1];
      edges[pair[1]].rev = pair[0];
    }
  }

  // ── 3. country lanes ──────────────────────────────────────────────────────
  let heads = 0;
  let cityJoins = 0;
  let midJoins = 0;

  for (const r of routes) {
    // A `head` carries only a circle centre. It needs no lane of its own: the
    // radial it caps already dead-ends there, and a dead end IS the U-turn (see
    // `connectNodes`). The circle is what makes that U-turn land on tarmac.
    if (r.kind === 'head') {
      heads++;
      continue;
    }
    let pts = r.points ?? [];
    if (pts.length < 2) continue;
    pts = pts.map((p) => ({ x: p.x, z: p.z }));

    // A route that starts on the plateau lip hands its first `CITY_JOIN_TRIM`
    // metres to the link that merges it into the boulevard — see the header.
    const nearA = nearestJunction(nodes, pts[0].x, pts[0].z, CITY_JOIN_RADIUS);
    const nearB = nearestJunction(nodes, pts[pts.length - 1].x, pts[pts.length - 1].z, CITY_JOIN_RADIUS);
    if (nearA >= 0) pts = trimPolyline(pts, CITY_JOIN_TRIM, false);
    if (nearB >= 0) pts = trimPolyline(pts, CITY_JOIN_TRIM, true);
    if (pts.length < 2) continue;

    // A route KIND that names a lane class is one (`highway` / `trunk` /
    // `dirt`); everything else is an ordinary `country` lane, links included.
    const cls = TIER_CLASSES.has(r.kind) ? r.kind : 'country';
    const off = LANE_OFFSET[cls] ?? LANE_OFFSET.country;
    const speed = SPEED_LIMIT[cls] ?? SPEED_LIMIT.country;
    // Joining the lattice means the country lane's terminal node IS the
    // junction — the link that `connectNodes` then builds spans the trimmed
    // metres and does the ±4.5 m -> ±2 m lateral taper in one smooth curve.
    const a = nearA >= 0 ? nearA : jointNode(pts[0].x, pts[0].z);
    const b = nearB >= 0 ? nearB : jointNode(pts[pts.length - 1].x, pts[pts.length - 1].z);
    if (nearA >= 0) cityJoins++;
    if (nearB >= 0) cityJoins++;

    // ── MID-POLYLINE JUNCTIONS ────────────────────────────────────────────
    // See the header. The offsets are taken over the WHOLE polyline and then
    // sliced, never per piece: two pieces that shared a centre-line vertex but
    // offset it from one-sided tangents would land ~7 cm apart with a couple of
    // degrees between them, which is a fillet where there should be nothing.
    // Sliced, the two pieces share the offset vertex EXACTLY, so `connectNodes`
    // takes its `DIRECT_GAP` path and the road stays one road.
    const n = pts.length;
    const rev = pts.slice().reverse();
    const fwdPts = offsetPolyline(pts, off);
    const bwdPts = offsetPolyline(rev, off);
    const cuts = splitIndices(pts, r.joins);
    midJoins += cuts.length;
    /** Boundary index -> node id, ends included. */
    const bounds = [{ i: 0, node: a }];
    for (const c of cuts) bounds.push({ i: c.i, node: jointNode(c.x, c.z) });
    bounds.push({ i: n - 1, node: b });

    for (let k = 1; k < bounds.length; k++) {
      const i0 = bounds[k - 1].i;
      const i1 = bounds[k].i;
      if (i1 - i0 < 1) continue;
      const p0 = bounds[k - 1].node;
      const p1 = bounds[k].node;
      const fwd = addEdge(p0, p1, fwdPts.slice(i0, i1 + 1), speed, cls, 'lane', { src: r.id });
      // `bwdPts[j]` is `pts[n-1-j]`, so this piece reversed is [n-1-i1, n-1-i0].
      const bwd = addEdge(p1, p0, bwdPts.slice(n - 1 - i1, n - i0), speed, cls, 'lane', { src: r.id });
      if (fwd < 0 || bwd < 0) continue;
      edges[fwd].rev = bwd;
      edges[bwd].rev = fwd;
    }
  }

  // ── 4. connect everything that meets at a node ────────────────────────────
  connectNodes(nodes, edges, addEdge);

  // ── 5. prune to the strongly connected core ───────────────────────────────
  const kept = strongestComponent(edges);
  const pruned = edges.length - kept.length;
  const compact = compactEdges(edges, kept);

  return new LaneGraph(nodes, compact, {
    pruned,
    connected: compact.length > 0,
    heads,
    cityJoins,
    midJoins,
    /** DRIVABLE lattice edges — `NO_TRAFFIC_CLASSES` are already gone. */
    latticeEdges: lattice.length,
    /** ...and how many were refused, so an alley cannot go missing silently. */
    latticeRefused: (map?.roads?.lattice?.length ?? 0) - lattice.length,
    countryRoutes: routes.length,
  });
}

/**
 * Where to cut a country route's lane polyline, from its published `joins`.
 *
 * ⚠ A VERTEX INDEX, AND THE NODE IS THE JOIN POINT — two separate decisions,
 * and both of them are the point of the mechanism. Cutting at a VERTEX is what
 * makes the two pieces share their offset endpoint exactly (see the call site);
 * noding on the JOIN POINT is what makes two different routes, cut at two
 * different vertices up to 2 m apart, land on ONE `jointNode`. Cut at the
 * projected point instead and the two pieces disagree by a fillet; node on the
 * vertex instead and a crossing becomes two nodes that never meet, with a
 * junction that simply is not there and nothing to report it.
 *
 * @param {Vec2XZ[]} pts The route's centre line, AFTER any city-join trim.
 * @param {Vec2XZ[]|undefined} joins
 * @returns {Array<{i: number, x: number, z: number}>} Ascending, de-crowded.
 */
function splitIndices(pts, joins) {
  if (!joins || !joins.length) return [];
  const n = pts.length;
  if (n < MID_JOIN_EDGE * 2 + 2) return [];
  /** @type {Array<{i: number, x: number, z: number}>} */
  const found = [];
  for (const j of joins) {
    let best = -1;
    let bestD = Infinity;
    for (let i = MID_JOIN_EDGE; i <= n - 1 - MID_JOIN_EDGE; i++) {
      const d = (pts[i].x - j.x) ** 2 + (pts[i].z - j.z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    // Too far to be this route's junction: the point is inside a city-join trim
    // or belongs to a corner `roundJoints` replaced. Leaving it out costs one
    // junction, never a wrong one.
    if (best < 0 || bestD > MID_JOIN_GAP * MID_JOIN_GAP) continue;
    found.push({ i: best, x: j.x, z: j.z });
  }
  found.sort((p, q) => p.i - q.i);
  /** @type {Array<{i: number, x: number, z: number}>} */
  const keep = [];
  for (const c of found) {
    if (keep.length && c.i - keep[keep.length - 1].i < MID_JOIN_SPAN) continue;
    keep.push(c);
  }
  return keep;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Half the junction box measured ALONG a heading. A lane leaving a junction
 * starts on the box boundary, which is what makes the link that crosses the box
 * exactly the paved crossing.
 * @param {LaneNode} n @param {number} dx @param {number} dz
 * @returns {number}
 */
function halfBox(n, dx, dz) {
  if (!n.box) return 0;
  return Math.abs(dx) >= Math.abs(dz) ? n.box.w / 2 : n.box.d / 2;
}

/**
 * @param {LaneNode[]} nodes @param {number} x @param {number} z @param {number} maxDist
 * @returns {number} Nearest JUNCTION node id, or −1.
 */
function nearestJunction(nodes, x, z, maxDist) {
  let best = -1;
  let bestD = maxDist;
  for (const n of nodes) {
    if (n.kind !== 'junction') continue;
    const d = Math.hypot(n.x - x, n.z - z);
    if (d < bestD) {
      bestD = d;
      best = n.id;
    }
  }
  return best;
}

/**
 * Drops `metres` of arc length off one end of a polyline, interpolating the new
 * terminal point so the trim is exact.
 * @param {Vec2XZ[]} pts @param {number} metres @param {boolean} fromEnd
 * @returns {Vec2XZ[]}
 */
function trimPolyline(pts, metres, fromEnd) {
  const src = fromEnd ? pts.slice().reverse() : pts;
  let left = metres;
  let i = 0;
  while (i + 1 < src.length) {
    const seg = Math.hypot(src[i + 1].x - src[i].x, src[i + 1].z - src[i].z);
    if (seg >= left) {
      const u = seg > 1e-9 ? left / seg : 0;
      const head = {
        x: src[i].x + (src[i + 1].x - src[i].x) * u,
        z: src[i].z + (src[i + 1].z - src[i].z) * u,
      };
      const out = [head, ...src.slice(i + 1)];
      return fromEnd ? out.reverse() : out;
    }
    left -= seg;
    i++;
  }
  // Shorter than the trim: keep the last two points so the route survives.
  const out = src.slice(-2);
  return fromEnd ? out.reverse() : out;
}

/**
 * Offsets a centre-line to the right of travel by `off`. The normal at a vertex
 * comes from the AVERAGED tangent of its two segments, so an inside corner does
 * not pinch.
 * @param {Vec2XZ[]} pts @param {number} off
 * @returns {Vec2XZ[]}
 */
function offsetPolyline(pts, off) {
  const n = pts.length;
  /** @type {Vec2XZ[]} */
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(n - 1, i + 1)];
    let tx = b.x - a.x;
    let tz = b.z - a.z;
    const l = Math.hypot(tx, tz);
    if (l > 1e-9) {
      tx /= l;
      tz /= l;
    } else {
      tx = 1;
      tz = 0;
    }
    out[i] = { x: pts[i].x - tz * off, z: pts[i].z + tx * off };
  }
  return out;
}

/**
 * Wires every edge that ENDS at a node to every edge that STARTS there,
 * inserting a link (a Bezier fillet) unless the two lanes already meet.
 *
 * The U-turn rule lives here: an arriving lane's own reverse is excluded unless
 * it is the ONLY thing leaving, which is exactly the definition of a dead end.
 *
 * @param {LaneNode[]} nodes @param {LaneEdge[]} edges @param {Function} addEdge
 */
function connectNodes(nodes, edges, addEdge) {
  /** @type {number[][]} */
  const arriving = nodes.map(() => []);
  /** @type {number[][]} */
  const leaving = nodes.map(() => []);
  const laneCount = edges.length; // links are appended below; never re-scanned
  for (let i = 0; i < laneCount; i++) {
    const e = edges[i];
    if (e.kind !== 'lane') continue;
    arriving[e.to].push(i);
    leaving[e.from].push(i);
  }

  for (let n = 0; n < nodes.length; n++) {
    const node = nodes[n];
    for (const inId of arriving[n]) {
      const inE = edges[inId];
      let outs = leaving[n].filter((id) => id !== inE.rev);
      if (outs.length === 0) outs = leaving[n].filter((id) => id === inE.rev);
      for (const outId of outs) {
        const outE = edges[outId];
        const p1 = inE.points[inE.points.length - 1];
        const p2 = outE.points[0];
        const d1 = endDir(inE);
        const d2 = startDir(outE);
        const gap = Math.hypot(p2.x - p1.x, p2.z - p1.z);
        const cross = d1.x * d2.z - d1.z * d2.x;
        const dot = d1.x * d2.x + d1.z * d2.z;
        const uTurn = dot < -0.5;

        // Two country lanes that already touch head-on need no fillet — and a
        // zero-length link would be an edge a car can never leave.
        if (!node.box && gap < DIRECT_GAP && Math.abs(cross) < DIRECT_TURN && dot > 0) {
          if (!inE.next.includes(outId)) inE.next.push(outId);
          continue;
        }

        const turn = uTurn ? -1 : cross > 0.25 ? 1 : cross < -0.25 ? -1 : 0;
        const speed = uTurn || turn !== 0
          ? Math.min(TURN_SPEED, inE.speedLimit, outE.speedLimit)
          : Math.min(LINK_SPEED, inE.speedLimit, outE.speedLimit);
        const pts = fillet(p1, d1, p2, d2, uTurn);
        const id = addEdge(n, n, pts, speed, 'link', 'link', {
          node: node.box ? n : -1,
          turn,
          uTurn,
          src: `${inE.id}>${outE.id}`,
        });
        if (id < 0) {
          if (!inE.next.includes(outId)) inE.next.push(outId);
          continue;
        }
        edges[id].next.push(outId);
        inE.next.push(id);
      }
    }
  }
}

/** @param {LaneEdge} e @returns {{x: number, z: number}} */
function endDir(e) {
  const n = e.points.length;
  return unit(e.points[n - 1].x - e.points[n - 2].x, e.points[n - 1].z - e.points[n - 2].z);
}

/** @param {LaneEdge} e @returns {{x: number, z: number}} */
function startDir(e) {
  return unit(e.points[1].x - e.points[0].x, e.points[1].z - e.points[0].z);
}

/** @param {number} x @param {number} z */
function unit(x, z) {
  const l = Math.hypot(x, z);
  return l > 1e-9 ? { x: x / l, z: z / l } : { x: 1, z: 0 };
}

/**
 * Cubic-Bezier fillet from `p1` (heading `d1`) to `p2` (heading `d2`).
 *
 * The handle length is 45% of the chord for a normal turn. A U-TURN needs a
 * longer one — its two tangents are opposed, so short handles collapse the
 * curve onto the chord and the car mirror-flips on the spot instead of swinging
 * round the turning circle.
 *
 * @param {Vec2XZ} p1 @param {{x: number, z: number}} d1
 * @param {Vec2XZ} p2 @param {{x: number, z: number}} d2
 * @param {boolean} uTurn
 * @returns {Vec2XZ[]}
 */
function fillet(p1, d1, p2, d2, uTurn) {
  const chord = Math.hypot(p2.x - p1.x, p2.z - p1.z);
  const k = uTurn ? Math.max(3.5, chord * 1.25) : Math.max(0.8, chord * 0.45);
  const c1 = { x: p1.x + d1.x * k, z: p1.z + d1.z * k };
  const c2 = { x: p2.x - d2.x * k, z: p2.z - d2.z * k };
  /** @type {Vec2XZ[]} */
  const pts = new Array(LINK_SAMPLES);
  for (let i = 0; i < LINK_SAMPLES; i++) {
    const t = i / (LINK_SAMPLES - 1);
    const mt = 1 - t;
    const a = mt * mt * mt;
    const b = 3 * mt * mt * t;
    const c = 3 * mt * t * t;
    const d = t * t * t;
    pts[i] = {
      x: a * p1.x + b * c1.x + c * c2.x + d * p2.x,
      z: a * p1.z + b * c1.z + c * c2.z + d * p2.z,
    };
  }
  return pts;
}

/**
 * Largest strongly connected component of the EDGE graph (successors are
 * `edge.next`), by iterative Tarjan — recursion would blow the stack on a
 * thousand-edge island.
 *
 * @param {LaneEdge[]} edges
 * @returns {number[]} Edge ids to keep, ascending.
 */
function strongestComponent(edges) {
  const n = edges.length;
  const index = new Int32Array(n).fill(-1);
  const low = new Int32Array(n);
  const onStack = new Uint8Array(n);
  /** @type {number[]} */
  const stack = [];
  let counter = 0;
  /** @type {number[]} */
  let best = [];

  for (let root = 0; root < n; root++) {
    if (index[root] >= 0) continue;
    /** @type {Array<[number, number]>} node + successor cursor */
    const work = [[root, 0]];
    index[root] = low[root] = counter++;
    stack.push(root);
    onStack[root] = 1;

    while (work.length) {
      const frame = work[work.length - 1];
      const v = frame[0];
      const succ = edges[v].next;
      if (frame[1] < succ.length) {
        const w = succ[frame[1]++];
        if (index[w] < 0) {
          index[w] = low[w] = counter++;
          stack.push(w);
          onStack[w] = 1;
          work.push([w, 0]);
        } else if (onStack[w]) {
          if (index[w] < low[v]) low[v] = index[w];
        }
      } else {
        work.pop();
        if (work.length) {
          const p = work[work.length - 1][0];
          if (low[v] < low[p]) low[p] = low[v];
        }
        if (low[v] === index[v]) {
          /** @type {number[]} */
          const comp = [];
          for (;;) {
            const w = stack.pop();
            onStack[w] = 0;
            comp.push(w);
            if (w === v) break;
          }
          if (comp.length > best.length) best = comp;
        }
      }
    }
  }
  return best.sort((a, b) => a - b);
}

/**
 * Renumbers the kept edges into a dense array and remaps every successor list.
 * @param {LaneEdge[]} edges @param {number[]} kept
 * @returns {LaneEdge[]}
 */
function compactEdges(edges, kept) {
  const remap = new Int32Array(edges.length).fill(-1);
  for (let i = 0; i < kept.length; i++) remap[kept[i]] = i;
  /** @type {LaneEdge[]} */
  const out = [];
  for (let i = 0; i < kept.length; i++) {
    const e = edges[kept[i]];
    e.id = i;
    e.next = e.next.map((id) => remap[id]).filter((id) => id >= 0);
    e.rev = e.rev >= 0 ? remap[e.rev] : -1;
    out.push(e);
  }
  return out;
}

/** @param {number} v @param {number} a @param {number} b */
function clampI(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/**
 * World yaw for a heading, in the vehicle stack's convention
 * (`Matrix4.makeRotationY(yaw)` maps +X to `(cos yaw, −sin yaw)`).
 * @param {number} dx @param {number} dz
 * @returns {number} Radians.
 */
export function yawFor(dx, dz) {
  return Math.atan2(-dz, dx);
}
